// ─────────────────────────────────────────────────────────────────
// Glass-i — Fixed Assets pure-JS helpers (Stage 6B.9D).
//
// No React, no axios, no JSX so node:test can import these directly.
// The JSX components re-export the constants + use the helpers
// verbatim.
// ─────────────────────────────────────────────────────────────────

// camelCase grid field → snake_case backend whitelist for the
// server-mode grid. Mirrors the backend's
// FIXED_ASSETS_GRID_ALLOWED_FIELDS keys.
export const GRID_FIELD_TO_SQL = Object.freeze({
  assetCode:                 'asset_code',
  assetName:                 'asset_name',
  categoryId:                'category_id',
  assetLedgerAccountId:      'asset_ledger_account_id',
  purchaseDate:              'purchase_date',
  capitalizationDate:        'capitalization_date',
  originalCost:              'original_cost',
  additionalCost:            'additional_cost',
  salvageValue:              'salvage_value',
  accumulatedDepreciation:   'accumulated_depreciation',
  depreciationMethod:        'depreciation_method',
  depreciationRate:          'depreciation_rate',
  usefulLifeYears:           'useful_life_years',
  status:                    'status',
  disposalDate:              'disposal_date',
  createdAt:                 'created_at',
  updatedAt:                 'updated_at'
});

export function mapField(c) {
  return c && typeof c === 'string' ? (GRID_FIELD_TO_SQL[c] || c) : c;
}

function mapRules(rules) {
  return rules.map((r) => {
    if (!r || typeof r !== 'object') return r;
    if (Array.isArray(r.rules)) return { ...r, rules: mapRules(r.rules) };
    if (typeof r.field === 'string' && r.field) return { ...r, field: mapField(r.field) };
    return r;
  });
}

export function translateQuery(q) {
  if (!q || typeof q !== 'object') return q;
  const out = { ...q };
  if (Array.isArray(out.filters)) {
    out.filters = out.filters.filter((f) => f && f.field)
      .map((f) => ({ ...f, field: mapField(f.field) }));
  }
  if (Array.isArray(out.sort)) {
    out.sort = out.sort.filter((s) => s && s.field)
      .map((s) => ({ ...s, field: mapField(s.field) }));
  }
  if (typeof out.groupBy === 'string' && out.groupBy) out.groupBy = mapField(out.groupBy);
  if (out.advancedFilters && Array.isArray(out.advancedFilters.rules)) {
    out.advancedFilters = {
      ...out.advancedFilters,
      rules: mapRules(out.advancedFilters.rules)
    };
  }
  return out;
}

// ── Constants surfaced to the modal ──────────────────────────────
export const DEPRECIATION_METHODS = Object.freeze([
  { value: 'wdv', label: 'Written-Down Value (WDV)' },
  { value: 'slm', label: 'Straight Line Method (SLM)' }
]);
export const ASSET_STATUSES = Object.freeze([
  { value: 'active',    label: 'Active',    color: 'green' },
  { value: 'sold',      label: 'Sold',      color: 'blue' },
  { value: 'scrapped',  label: 'Scrapped',  color: 'red' },
  { value: 'retired',   label: 'Retired',   color: 'default' }
]);
export const STATUS_COLOR = Object.freeze({
  active: 'green', sold: 'blue', scrapped: 'red', retired: 'default'
});
export const STATUS_LABEL = Object.freeze({
  active: 'Active', sold: 'Sold', scrapped: 'Scrapped', retired: 'Retired'
});

export const DISPOSAL_STATUSES = Object.freeze([
  { value: 'sold',     label: 'Sold' },
  { value: 'scrapped', label: 'Scrapped' },
  { value: 'retired',  label: 'Retired' }
]);

// ── Pure helpers ─────────────────────────────────────────────────
function toNumber(v) {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function round2(n) {
  return Math.round((toNumber(n) + Number.EPSILON) * 100) / 100;
}

// WDV calculator — kept in lockstep with backend `writtenDownValue`.
export function calculateWdv({ originalCost, additionalCost, accumulatedDepreciation }) {
  return round2(toNumber(originalCost) + toNumber(additionalCost)
    - toNumber(accumulatedDepreciation));
}

// Depreciable base = cost + additional - salvage (≥ 0).
export function calculateDepreciableBase({ originalCost, additionalCost, salvageValue }) {
  return Math.max(0, round2(toNumber(originalCost) + toNumber(additionalCost)
    - toNumber(salvageValue)));
}

// Compute the gain/loss on disposal preview from current asset state.
//   wdv  = gross - accum
//   gain = sale - wdv   (positive = gain, negative = loss)
export function calculateDisposalPreview({
  originalCost, additionalCost, accumulatedDepreciation, saleValue
}) {
  const wdv = calculateWdv({ originalCost, additionalCost, accumulatedDepreciation });
  const sale = round2(saleValue);
  const gainLoss = round2(sale - wdv);
  return { wdv, saleValue: sale, gainLossOnDisposal: gainLoss,
    isGain: gainLoss > 0, isLoss: gainLoss < 0 };
}

// INR thousand-separated 2dp formatter.
export function formatRupee(n) {
  const v = toNumber(n);
  return v.toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  });
}

// Client-side validation mirroring the backend Joi schema. Returns an
// array of friendly messages (empty when the form is valid).
export function findAssetFormErrors(form) {
  const errs = [];
  if (!form) return ['Form values are required.'];
  if (!form.assetName)              errs.push('Asset name is required.');
  if (!form.assetLedgerAccountId)   errs.push('Select a Fixed Asset ledger.');
  if (!form.purchaseDate)           errs.push('Purchase date is required.');
  if (!form.capitalizationDate)     errs.push('Capitalization date is required.');
  if (form.purchaseDate && form.capitalizationDate) {
    const p = new Date(form.purchaseDate).getTime();
    const c = new Date(form.capitalizationDate).getTime();
    if (Number.isFinite(p) && Number.isFinite(c) && c < p) {
      errs.push('Capitalization date cannot be before purchase date.');
    }
  }
  if (toNumber(form.originalCost) < 0)   errs.push('Original cost must be zero or greater.');
  if (toNumber(form.additionalCost) < 0) errs.push('Additional cost must be zero or greater.');
  if (toNumber(form.salvageValue) < 0)   errs.push('Salvage value must be zero or greater.');
  if (form.depreciationRate != null && form.depreciationRate !== ''
      && toNumber(form.depreciationRate) < 0) {
    errs.push('Depreciation rate must be zero or greater.');
  }
  if (form.usefulLifeYears != null && form.usefulLifeYears !== ''
      && !(toNumber(form.usefulLifeYears) > 0)) {
    errs.push('Useful life (years) must be greater than zero when provided.');
  }
  return errs;
}

export function findDisposalFormErrors(form) {
  const errs = [];
  if (!form) return ['Form values are required.'];
  if (!form.disposalDate) errs.push('Disposal date is required.');
  if (toNumber(form.saleValue) < 0) errs.push('Sale value must be zero or greater.');
  if (toNumber(form.saleValue) > 0 && !form.receivingAccountId) {
    errs.push('A receiving account is required when the sale value is greater than zero.');
  }
  if (form.status && !['sold','scrapped','retired'].includes(form.status)) {
    errs.push('Disposal status must be sold, scrapped, or retired.');
  }
  return errs;
}

export function findDepreciationFormErrors(form) {
  const errs = [];
  if (!form) return ['Form values are required.'];
  if (!form.fromDate) errs.push('Depreciation from-date is required.');
  if (!form.toDate)   errs.push('Depreciation to-date is required.');
  if (form.fromDate && form.toDate) {
    const f = new Date(form.fromDate).getTime();
    const t = new Date(form.toDate).getTime();
    if (Number.isFinite(f) && Number.isFinite(t) && t < f) {
      errs.push('Depreciation to-date cannot be before from-date.');
    }
  }
  return errs;
}

// Translate a caught error into a friendly string. Reuses the
// accounting error envelope: `error.response.data.error.{code,message}`.
const FA_CODE_OVERRIDES = Object.freeze({
  FA_NOT_FOUND:                       'Fixed asset not found.',
  FA_CATEGORY_NOT_FOUND:              'Fixed asset category not found.',
  FA_CATEGORY_IN_USE:                 null,
  FA_CODE_DUPLICATE:                  null,
  FA_CATEGORY_CODE_DUPLICATE:         null,
  FA_LEDGER_REQUIRED:                 null,
  FA_LEDGER_NOT_FOUND:                'Selected ledger is missing or has been deleted.',
  FA_LEDGER_INACTIVE:                 'Selected ledger is inactive.',
  FA_LEDGER_WRONG_TYPE:               null,
  FA_LEDGER_NOT_FIXED_ASSET:          'Asset ledger must be an active Fixed Asset ledger.',
  FA_LEDGER_NOT_DEPRECIATION_EXPENSE: 'Depreciation expense ledger must be an active Expense ledger.',
  FA_NOT_DELETABLE:                   null,
  FA_HAS_DEPRECIATION:                'Asset has posted depreciation. It cannot be deleted — dispose it instead.',
  FA_NOT_DEPRECIABLE:                 'Asset is already disposed and cannot be depreciated.',
  FA_NOT_DISPOSABLE:                  'Asset is already disposed.',
  FA_DEPR_EXP_LEDGER_MISSING:         'Select a Depreciation Expense ledger before posting depreciation.',
  FA_ACCUM_LEDGER_MISSING:            'Select an Accumulated Depreciation ledger before posting depreciation.',
  FA_DEPR_AMOUNT_ZERO:                'Computed depreciation amount is zero. Check method, rate, useful life, and the date range.',
  FA_DISPOSAL_LOSS_LEDGER_MISSING:    'Loss on disposal cannot be booked because no offset ledger is configured.',
  FA_DISPOSAL_GAIN_LEDGER_MISSING:    'Gain on disposal cannot be booked because no offset ledger is configured.',
  FA_DISPOSAL_UNBALANCED:             'Disposal voucher is not balanced. Please refresh the asset and try again.',
  FA_SEQUENCE_MISSING:                'Fixed Asset code sequence is not configured. Please create document sequences in Accounts Setup.',
  FA_RACE_LOST:                       'Asset changed concurrently. Refresh and retry.',
  FA_NOT_EDITABLE:                    null
});

export function friendlyErrorFor(err) {
  if (!err) return 'Something went wrong.';
  const body = err && err.response && err.response.data && err.response.data.error
    ? err.response.data.error : null;
  const code = body && body.code;
  if (code && Object.prototype.hasOwnProperty.call(FA_CODE_OVERRIDES, code)
      && FA_CODE_OVERRIDES[code]) {
    return FA_CODE_OVERRIDES[code];
  }
  return (body && body.message)
    || err.message
    || 'Request failed.';
}
